import React, { useState } from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ChevronDown, ChevronRight, Eye, EyeOff, Lock, LockOpen, Trash2, Copy } from 'lucide-react';
import { useSceneStore, useSelectionStore } from '../../stores';
import { SceneObject } from '../../types';
import { cn } from '@/lib/utils';

interface ObjectItemProps {
  object: SceneObject;
  level: number;
  isSelected: boolean;
  onSelect: (objectId: string) => void;
}

const ObjectItem: React.FC<ObjectItemProps> = ({ object, level, isSelected, onSelect }) => {
  const { updateObject, deleteObject, duplicateObject, getChildObjects } = useSceneStore();
  const [isExpanded, setIsExpanded] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(object.name);

  const children = getChildObjects(object.id);
  const hasChildren = children.length > 0;

  const handleToggleVisibility = (e: React.MouseEvent) => {
    e.stopPropagation();
    updateObject(object.id, { visible: !object.visible });
  };

  const handleToggleLock = (e: React.MouseEvent) => {
    e.stopPropagation();
    updateObject(object.id, { locked: !object.locked });
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    deleteObject(object.id);
  };

  const handleDuplicate = (e: React.MouseEvent) => {
    e.stopPropagation();
    duplicateObject(object.id);
  };

  const handleNameSubmit = () => {
    if (editName.trim()) {
      updateObject(object.id, { name: editName.trim() });
    }
    setIsEditing(false);
  };

  const handleNameKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleNameSubmit();
    } else if (e.key === 'Escape') {
      setEditName(object.name);
      setIsEditing(false);
    }
  };

  const getObjectIcon = (obj: SceneObject) => {
    switch (obj.type) {
      case 'device':
        switch ((obj.properties as any).deviceType) {
          case 'temperature-sensor':
            return '🌡️';
          case 'humidity-sensor':
            return '💧';
          case 'motion-sensor':
            return '🏃';
          case 'pressure-sensor':
            return '🎯';
          case 'camera':
            return '📷';
          case 'gateway':
            return '📡';
          case 'actuator':
            return '⚙️';
          default:
            return '📟';
        }
      case 'primitive':
        switch ((obj.properties as any).geometry) {
          case 'box':
            return '📦';
          case 'sphere':
            return '⚪';
          case 'cylinder':
            return '🥫';
          case 'plane':
            return '▬';
          default:
            return '📐';
        }
      case 'group':
        return '📁';
      default:
        return '📄';
    }
  };

  return (
    <div>
      <div
        className={cn(
          'flex items-center py-1 px-2 rounded cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors',
          isSelected && 'bg-blue-100 dark:bg-blue-900',
          !object.visible && 'opacity-50',
        )}
        style={{ paddingLeft: `${level * 16 + 8}px` }}
        onClick={() => onSelect(object.id)}
      >
        {/* Expand/collapse button */}
        <div className="w-4 h-4 flex items-center justify-center mr-1">
          {hasChildren && (
            <Button
              variant="ghost"
              size="sm"
              className="w-4 h-4 p-0"
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(!isExpanded);
              }}
            >
              {isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            </Button>
          )}
        </div>

        {/* Object icon */}
        <span className="text-sm mr-2">{getObjectIcon(object)}</span>

        {/* Object name */}
        <div className="flex-1 min-w-0">
          {isEditing ? (
            <Input
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              onBlur={handleNameSubmit}
              onKeyDown={handleNameKeyPress}
              className="h-6 text-xs"
              autoFocus
            />
          ) : (
            <span className="text-xs truncate cursor-pointer" onDoubleClick={() => setIsEditing(true)}>
              {object.name}
            </span>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button
            variant="ghost"
            size="sm"
            className="w-6 h-6 p-0"
            onClick={handleToggleVisibility}
            title={object.visible ? 'Hide' : 'Show'}
          >
            {object.visible ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
          </Button>

          <Button
            variant="ghost"
            size="sm"
            className="w-6 h-6 p-0"
            onClick={handleToggleLock}
            title={object.locked ? 'Unlock' : 'Lock'}
          >
            {object.locked ? <Lock className="w-3 h-3" /> : <LockOpen className="w-3 h-3" />}
          </Button>

          <Button variant="ghost" size="sm" className="w-6 h-6 p-0" onClick={handleDuplicate} title="Duplicate">
            <Copy className="w-3 h-3" />
          </Button>

          <Button
            variant="ghost"
            size="sm"
            className="w-6 h-6 p-0 text-red-500 hover:text-red-700"
            onClick={handleDelete}
            title="Delete"
          >
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      </div>

      {/* Children */}
      {hasChildren && isExpanded && (
        <div>
          {children.map((child) => (
            <ObjectItem key={child.id} object={child} level={level + 1} isSelected={isSelected} onSelect={onSelect} />
          ))}
        </div>
      )}
    </div>
  );
};

export const SceneHierarchy: React.FC = () => {
  const { currentScene } = useSceneStore();
  const { selectedObjectIds, selectObject, deselectAll } = useSelectionStore();
  const [searchTerm, setSearchTerm] = useState('');

  if (!currentScene) {
    return <div className="p-4 text-center text-gray-500">No scene loaded</div>;
  }

  // Get root objects (objects without parents)
  const rootObjects = currentScene.objects.filter((obj) => !obj.parent);

  // Filter objects based on search term
  const filteredObjects = rootObjects.filter((obj) => obj.name.toLowerCase().includes(searchTerm.toLowerCase()));

  const handleObjectSelect = (objectId: string) => {
    selectObject(objectId);
  };

  return (
    <div className="h-full flex flex-col">
      <div className="p-4 border-b">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold">Scene Hierarchy</h3>
          <Button variant="ghost" size="sm" onClick={deselectAll} className="text-xs">
            Clear Selection
          </Button>
        </div>
        <Input
          placeholder="Search objects..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full"
        />
      </div>

      <ScrollArea className="flex-1">
        <div className="p-2 space-y-1">
          {filteredObjects.length === 0 ? (
            <div className="p-4 text-center text-gray-500 text-sm">
              {searchTerm ? 'No matching objects' : 'No objects in scene'}
            </div>
          ) : (
            filteredObjects.map((object) => (
              <div key={object.id} className="group">
                <ObjectItem
                  object={object}
                  level={0}
                  isSelected={selectedObjectIds.includes(object.id)}
                  onSelect={handleObjectSelect}
                />
              </div>
            ))
          )}
        </div>
      </ScrollArea>

      <div className="p-3 border-t bg-gray-50 dark:bg-gray-800">
        <div className="text-xs text-gray-500">
          {currentScene.objects.length} object{currentScene.objects.length !== 1 ? 's' : ''}
          {selectedObjectIds.length > 0 && <span> • {selectedObjectIds.length} selected</span>}
        </div>
      </div>
    </div>
  );
};
